import {
	IsBoolean,
	IsEnum,
	IsNotEmpty,
	IsOptional,
	IsString,
	IsUUID,
	MaxLength,
	Validate,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';
import { NewsfeedTargetAudience } from '../../enums';
import { Transform } from 'class-transformer';
import { NEWSFEED_TITLE_MAX_LENGTH } from '../../constants/newsfeed.constants';

const NEWSFEED_NOTIFICATION_FIELDS = [
	'notification_header',
	'notification_message',
	'notification_icon_img_parent_id',
] as const;

@ValidatorConstraint({ name: 'AllNewsfeedNotificationFieldsOrNone' })
class AllNewsfeedNotificationFieldsOrNoneConstraint implements ValidatorConstraintInterface {
	validate(_value: unknown, args: ValidationArguments): boolean {
		const dto = args.object as Record<string, unknown>;
		const filledCount = NEWSFEED_NOTIFICATION_FIELDS.filter((field) => dto[field] != null).length;
		return filledCount === 0 || filledCount === NEWSFEED_NOTIFICATION_FIELDS.length;
	}

	defaultMessage(): string {
		return 'Newsfeed notification fields must be either all filled or all null.';
	}
}

export class UpsertNewsfeedTitleDto {
	@IsUUID()
	@IsNotEmpty()
	event_id: string;

	@IsString()
	@IsNotEmpty()
	@Transform(({ value }) => (typeof value === 'string' ? value.trim() : value))
	@MaxLength(NEWSFEED_TITLE_MAX_LENGTH)
	title: string;
}

export class GetNewsfeedDto {
	@IsUUID()
	@IsNotEmpty()
	event_id: string;
}

export class CreateNewsfeedArticleDto {
	@IsUUID()
	@IsNotEmpty()
	event_id: string;

	@IsUUID()
	@IsNotEmpty()
	img_parent_id: string;

	@IsOptional()
	img_variants?: Record<string, string>;

	@IsString()
	@IsNotEmpty()
	tag: string;

	@IsString()
	@IsNotEmpty()
	header: string;

	@IsString()
	@IsNotEmpty()
	@MaxLength(140)
	description: string;

	@IsString()
	@IsNotEmpty()
	cta_label: string;

	@IsString()
	@IsNotEmpty()
	cta_link: string;

	@IsString()
	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_header?: string;

	@IsString()
	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_message?: string;

	@IsUUID()
	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_icon_img_parent_id?: string;

	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_icon_img_variants?: Record<string, string>;

	@IsEnum(NewsfeedTargetAudience)
	@IsOptional()
	target_audience?: NewsfeedTargetAudience;
}

/** Same fields as CreateNewsfeedArticleDto but nothing is required — `id` comes from the route param. */
export class UpdateNewsfeedArticleDto {
	/** Explicitly notify again after an admin edits an article. */
	@IsBoolean()
	@IsOptional()
	renotify?: boolean;

	@IsUUID()
	@IsOptional()
	event_id?: string;

	@IsUUID()
	@IsOptional()
	img_parent_id?: string;

	@IsOptional()
	img_variants?: Record<string, string>;

	@IsString()
	@IsOptional()
	tag?: string;

	@IsString()
	@IsOptional()
	header?: string;

	@IsString()
	@IsOptional()
	@MaxLength(140)
	description?: string;

	@IsString()
	@IsOptional()
	cta_label?: string;

	@IsString()
	@IsOptional()
	cta_link?: string;

	@IsString()
	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_header?: string;

	@IsString()
	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_message?: string;

	@IsUUID()
	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_icon_img_parent_id?: string;

	@IsOptional()
	@Validate(AllNewsfeedNotificationFieldsOrNoneConstraint)
	notification_icon_img_variants?: Record<string, string>;

	@IsEnum(NewsfeedTargetAudience)
	@IsOptional()
	target_audience?: NewsfeedTargetAudience;
}

export class NewsfeedArticleDto {
	id: string;
	event_id: string;
	img_parent_id: string | null;
	img_variants: Record<string, string> | null;
	tag: string;
	header: string;
	description: string;
	cta_label: string;
	cta_link: string;
	created_date: Date;
	notification_header?: string | null;
	notification_message?: string | null;
	notification_icon_img_parent_id?: string | null;
	notification_icon_img_variants?: Record<string, string> | null;
}

/** Save outcome only; notification warnings are not persisted on the article. */
export class SaveNewsfeedArticleResponseDto extends NewsfeedArticleDto {
	notification_warning?: string;
}

export class NewsfeedResponseDto {
	title: string | null;
	articles: NewsfeedArticleDto[];
}

export class GetNewsfeedArticleNotificationDto {
	@IsUUID()
	@IsNotEmpty()
	article_id: string;
}

export class NewsfeedArticleNotificationDto {
	notificationId: string;
	createdDate: string;
	articleId: string;
	header: string;
	message: string;
	icon_img_parent_id: string;
	icon_img_variants: Record<string, string> | null;
}

export class GetUnreadNewsfeedArticleNotificationsDto {
	@IsUUID()
	@IsNotEmpty()
	event_id: string;
}

export class MarkNewsfeedArticleSeenDto {
	@IsUUID()
	@IsNotEmpty()
	article_id: string;

	@IsOptional()
	@IsUUID()
	notification_id?: string;
}
